/**
 * Patient Profile management.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { patientAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const EDITABLE_FIELDS = [
  'full_name',
  'phone_number',
  'date_of_birth',
  'gender',
  'county',
  'sub_county',
  'chronic_conditions',
  'emergency_contact_name',
  'emergency_contact_phone',
  'blood_type',
  'allergies',
];

const PatientProfile = () => {
  const { userProfile, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadProfile = async () => {
    try {
      const res = await patientAPI.getProfile();
      setProfile(res.data.profile || res.data);
    } catch {
      setProfile(userProfile || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, [userProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = EDITABLE_FIELDS.reduce((acc, key) => {
        if (profile?.[key] !== undefined) {
          acc[key] = profile[key];
        }
        return acc;
      }, {});

      await patientAPI.updateProfile(payload);
      await loadProfile();
      toast.success('Profile updated successfully.');
      setEditing(false);
    } catch (error) {
      toast.error(error.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Profile" role="patient" showBack>
        <div className="p-4 space-y-4">
          {[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-2xl" />)}
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Profile" role="patient" showBack>
      <div className="p-4 pb-24 space-y-4">
        {/* Avatar & Name */}
        <div className="card flex items-center gap-4">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-primary-700 text-2xl font-bold">
              {(profile?.full_name || 'P').charAt(0)}
            </span>
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">{profile?.full_name}</h2>
            <p className="text-sm text-gray-500">{profile?.email}</p>
            <span className="badge-primary text-xs">Patient</span>
          </div>
        </div>

        {/* Profile Fields */}
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Personal Information</h3>
            <button onClick={() => setEditing(!editing)} className="text-primary-500 text-sm font-medium">
              {editing ? 'Cancel' : 'Edit'}
            </button>
          </div>

          {[
            { label: 'Full Name', key: 'full_name' },
            { label: 'Phone Number', key: 'phone_number' },
            { label: 'County', key: 'county' },
            { label: 'Sub-County', key: 'sub_county' },
            { label: 'Date of Birth', key: 'date_of_birth', type: 'date' },
          ].map(field => (
            <div key={field.key}>
              <label className="label">{field.label}</label>
              {editing ? (
                <input
                  type={field.type || 'text'}
                  value={profile?.[field.key] || ''}
                  onChange={e => setProfile(p => ({ ...p, [field.key]: e.target.value }))}
                  className="input-field"
                />
              ) : (
                <p className="text-gray-900">{profile?.[field.key] || '—'}</p>
              )}
            </div>
          ))}

          {editing && (
            <button onClick={handleSave} disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2">
              {saving && <div className="spinner w-5 h-5 border-2"></div>}
              Save Changes
            </button>
          )}
        </div>

        {/* Chronic Conditions */}
        <div className="card">
          <h3 className="font-semibold text-gray-900 mb-3">Chronic Conditions</h3>
          <div className="flex flex-wrap gap-2">
            {(profile?.chronic_conditions || []).length > 0 ? (
              profile.chronic_conditions.map((c, i) => (
                <span key={i} className="px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full">{c}</span>
              ))
            ) : (
              <p className="text-gray-400 text-sm">No conditions recorded</p>
            )}
          </div>
        </div>

        {/* Logout */}
        <button onClick={logout} className="btn-secondary w-full text-red-600">
          Sign Out
        </button>
      </div>
    </Layout>
  );
};

export default PatientProfile;
